import React, { useEffect, useState } from "react";
import {
  useTable,
  useSortBy,
  useGlobalFilter,
  useAsyncDebounce,
} from "react-table";

import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Flex,
  Spinner,
  Box,
  Input,
  Button,
  Link,
  Text,
  Heading,
} from "@chakra-ui/react";
import {
  ChevronUpIcon,
  ChevronDownIcon,
  CheckCircleIcon,
} from "@chakra-ui/icons";
import { getLocationsList } from "./utils/api";
import { PROGRAM_TYPES } from "./constants/programTypes";
import ReactTimeAgo from "react-time-ago";
import moment from "moment";
import chakraTheme from "./constants/chakraTheme";
import placeholderGlobalEntryData from "./constants/placeholder/placeholderGlobalEntryData";
import placeholderSentriData from "./constants/placeholder/placeholderSentriData";
import placeholdeNexusData from "./constants/placeholder/placeholdeNexusData";
import { rootDomain } from "./utils/domainUtils";
import { programTypeToName, programTypeToUrlString } from "./utils/programs";

const programTypeToDefaultData = {
  GLOBAL_ENTRY: placeholderGlobalEntryData.locations,
  SENTRI: placeholderSentriData.locations,
  NEXUS: placeholdeNexusData.locations,
};

const PAGE_STATES = {
  LOADING: "LOADING",
  SUCCESS: "SUCCESS",
  ERROR: "ERROR",
};
// Define a default UI for filtering
function GlobalFilter({
  preGlobalFilteredRows,
  globalFilter,
  setGlobalFilter,
}) {
  const count = preGlobalFilteredRows.length;
  const [value, setValue] = React.useState(globalFilter);
  const onChange = useAsyncDebounce((value) => {
    setGlobalFilter(value || undefined);
  }, 200);

  return (
    <Box bg="gray.50" m="10px">
      <Input
        w="100%"
        value={value || ""}
        onChange={(e) => {
          setValue(e.target.value);
          onChange(e.target.value);
        }}
        placeholder={`Search...`}
        autoFocus
      />
    </Box>
  );
}

const range = (len) => {
  const arr = [];
  for (let i = 0; i < len; i++) {
    arr.push(i);
  }
  return arr;
};

const newPerson = () => {
  const statusChance = Math.random();
  return {
    firstName: "none",
    lastName: "none",
    age: Math.floor(Math.random() * 30),
    visits: Math.floor(Math.random() * 100),
    progress: Math.floor(Math.random() * 100),
    status:
      statusChance > 0.66
        ? "relationship"
        : statusChance > 0.33
        ? "complicated"
        : "single",
  };
};

const Cell = ({ cell, row, programType }) => {
  if (cell.column.id === "urlString") {
    if (cell?.row?.original?.waitTimeDays > 30) {
      return (
        <Td {...cell.getCellProps()}>
          <Button as={Link} href="https://appointmentscanner.com?ref=waittimes">
            Find a sooner interview
          </Button>
        </Td>
      );
    }
    // else if (cell?.row?.original?.waitTimeDays > 14) {
    //   return (
    //     <Td {...cell.getCellProps()}>
    //       <Button as={Link} href={`${window.location.pathname}/${cell.value}`}>
    //         Find a sooner interview
    //       </Button>
    //     </Td>
    //   );
    // }
    else {
      return (
        <Td {...cell.getCellProps()}>
          Lots of appointments available. You can book an interview on your own.
        </Td>
      );
    }
  } else if (cell.column.id === "info") {
    return (
      <Td {...cell.getCellProps()}>
        <Button
          as={Link}
          href={`${rootDomain}/${programTypeToUrlString[programType]}/${cell?.row?.original?.urlString}`}
        >
          Info
        </Button>
      </Td>
    );
  } else if (cell.column.id === "waitTimeDays") {
    let color, text;
    if (cell.value > 365 / 2) {
      color = chakraTheme.colors.red[200];
      if (cell.value >= 365) {
        text = "1 year";
      } else {
        text = `${cell.value} days`;
      }
    } else if (cell.value > 30) {
      color = chakraTheme.colors.yellow[200];
      text = `${cell.value} days`;
    } else {
      color = chakraTheme.colors.green[100];
      if (cell.value === 1) {
        text = `${cell.value} day`;
      } else {
        text = `${cell.value} days`;
      }
    }
    return (
      <Td {...cell.getCellProps()} bg={color}>
        {text}
      </Td>
    );
  } else {
    return <Td {...cell.getCellProps()}>{cell.render("Cell")}</Td>;
  }
};

function WaitTimesTable({
  programType,
  locationData: locationDataProp,
  setLocationData,
  updatedAt,
  pageState,
}) {
  let locationData;
  if (!locationDataProp) {
    locationData = programTypeToDefaultData[programType];
  } else {
    locationData = locationDataProp;
  }

  const columns = React.useMemo(
    () => [
      {
        Header: "Country",
        accessor: "countryCode",
      },
      {
        Header: "State",
        accessor: "state",
      },
      {
        Header: "City",
        accessor: "city",
      },
      {
        Header: "Location",
        accessor: "name",
      },
      {
        Header: "Interview wait time",
        accessor: "waitTimeDays",
      },
      {
        Header: "Info",
        accessor: "info",
        // render: ({ cell }) => <Button as={Link} href={`/${cell}`} />,
      },
      {
        Header: "Next steps",
        accessor: "urlString",
        // render: ({ cell }) => <Button as={Link} href={`/${cell}`} />,
      },
    ],
    []
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    state,
    preGlobalFilteredRows,
    setGlobalFilter,
  } = useTable(
    {
      columns,
      data: locationData,
    },
    useGlobalFilter,
    useSortBy
  );

  // We don't want to render all 2000 rows for this example, so cap
  // it at 20 for this use case

  // if (pageState === PAGE_STATES.LOADING) {
  //   return (
  //     <Flex>
  //       <Text as="p" fontSize="20pt">
  //         Loading wait times...
  //       </Text>
  //     </Flex>
  //   );
  // }

  return (
    <Box
      bg="white"
      borderRadius="md"
      textAlign="center"
      pt="10px"
      overflowX="scroll"
      maxW="100vw"
    >
      <Heading as="h1" size="lg" my="10" zIndex={10000}>
        {programTypeToName[programType]} Enrollment Center Interview Wait Times
      </Heading>
      {programType === PROGRAM_TYPES.GLOBAL_ENTRY && (
        <Box textAlign="left" px="10" pb="5">
          <Text mb="2">
            If you’re doing a lot of international travel and want to get Global
            Entry membership, you’ll find that Global Entry interview
            appointments are hard to come by.
          </Text>
          <Text mb="2">
            Below you’ll find a list of Global Entry enrollment centers and how
            long it is until the next available interview.
          </Text>
          <Text mb="2">
            These locations allow you to interview for at least one of the
            Department of Homeland Security’s trusted traveler programs (TTP)
            including Global Entry, TSA Precheck, Nexus, and Sentri.
          </Text>
          Use Appointment Scanner to get notified when a Global Entry interview
          appointment slot opens up at your chosen enrollment center.
          <Text mb="2">
            Visit cbp.gov for more information about the Global Entry programs
            and the Global Entry application.
          </Text>
        </Box>
      )}

      {updatedAt && (
        <Flex p="10px" pt="10px" alignItems="center">
          <CheckCircleIcon size="sm" color="green.500" mr="10px" />
          <Text mr="5px">Updated</Text>
          <ReactTimeAgo date={moment(updatedAt).toDate()} locale="en-US" />
        </Flex>
      )}
      {!updatedAt && (
        <Flex p="10px" pt="10px" alignItems="center">
          <Spinner size="xs" color="blue.500" mr="10px" />
          {/* <CheckCircleIcon size="sm" color="blue.500" mr="10px" /> */}
          <Text mr="5px">Updating</Text>
        </Flex>
      )}
      <GlobalFilter
        preGlobalFilteredRows={preGlobalFilteredRows}
        globalFilter={state.globalFilter}
        setGlobalFilter={setGlobalFilter}
      />
      <Table {...getTableProps()} bg="white" overflowX="scroll" maxW="100vw">
        <Thead>
          {headerGroups.map((headerGroup) => (
            <Tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column) => (
                // Add the sorting props to control sorting. For this example
                // we can add them into the header props
                <Th
                  userSelect="none"
                  {...column.getHeaderProps(column.getSortByToggleProps())}
                >
                  <Flex alignItems="center">
                    {column.render("Header")}
                    {/* Add a sort direction indicator */}
                    {column.isSorted ? (
                      column.isSortedDesc ? (
                        <ChevronDownIcon ml={1} w={4} h={4} />
                      ) : (
                        <ChevronUpIcon ml={1} w={4} h={4} />
                      )
                    ) : (
                      ""
                    )}
                  </Flex>
                </Th>
              ))}
            </Tr>
          ))}
        </Thead>
        <Tbody {...getTableBodyProps()}>
          {rows.map((row, i) => {
            prepareRow(row);
            return (
              <Tr {...row.getRowProps()}>
                {row.cells.map((cell, j) => {
                  return (
                    <Cell cell={cell} key={i + j} programType={programType} />
                  );
                })}
              </Tr>
            );
          })}
        </Tbody>
      </Table>
    </Box>
  );
}

export default WaitTimesTable;
